import { Button, Modal, Select, Typography } from 'antd';
import { AWSError, CognitoIdentityServiceProvider } from 'aws-sdk';
import { UserType } from 'aws-sdk/clients/cognitoidentityserviceprovider';
import { PromiseResult } from 'aws-sdk/lib/request';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { setAWSCreds } from '../utils/accessToken';
import { readWebsiteVariable } from '../utils/readWebsiteVariable';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  envId: string;
  setEnvId: (envId: string) => void;
}

const AdminTools: React.FC<Props> = ({ open, setOpen, envId, setEnvId }) => {
  const history = useHistory();
  const [cognitoUsers, setCognitoUsers] = useState<UserType[]>([]);
  const [searchResults, setSearchResults] = useState<UserType[]>([]);
  const [searchInputValue, setSearchInputValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchAllCognitoUsers = async () => {
    await setAWSCreds();
    const userPoolId = readWebsiteVariable('REACT_APP_aws_user_pools_id');
    const cog = new CognitoIdentityServiceProvider();
    let paginationToken;
    let users: UserType[] = [];
    do {
      const res: PromiseResult<
        CognitoIdentityServiceProvider.ListUsersResponse,
        AWSError
      > = await cog
        .listUsers({
          UserPoolId: userPoolId,
          Limit: 60,
          PaginationToken: paginationToken
        })
        .promise();
      if (res.Users) users = [...users, ...res.Users];
      paginationToken = res.PaginationToken;
    } while (paginationToken);

    users.sort((a, b) => {
      return (
        (b.UserCreateDate?.getTime() ?? 0) - (a.UserCreateDate?.getTime() ?? 0)
      );
    });

    return users;
  };

  const adminEnvId = localStorage.getItem('adminInEnvId');

  useEffect(() => {
    if (!loaded && open) {
      setLoaded(true);
      setLoading(true);
      fetchAllCognitoUsers().then((users) => {
        setCognitoUsers(users);
        setSearchResults(users.slice(0, 100)); // show the last 100 by default
        setLoading(false);
      });
    }
  }, [loaded, open]);

  const handleSearch = (search: string) => {
    if (search && cognitoUsers) {
      setSearchResults(
        cognitoUsers.filter((u) => {
          const email = u.Attributes?.find((a) => a.Name === 'email')?.Value;
          const envId = u.Attributes?.find(
            (a) => a.Name === 'custom:envIds'
          )?.Value;
          return (
            (envId && envId.includes(search)) ||
            (email && email.includes(search))
          );
        })
      );
    } else {
      setSearchResults(cognitoUsers.slice(0, 100)); // show the last 100 by default
    }
  };

  return (
    <Modal
      open={open}
      title="Switch Account"
      footer={[
        <Button key="admin_modal_close" onClick={() => setOpen(false)}>
          Close
        </Button>,
        <Button
          key="admin_modal_switch_out"
          type="primary"
          ghost
          disabled={!localStorage.getItem('adminInEnvId')}
          onClick={() => {
            localStorage.removeItem('adminInEnvId');
            document.location.href = '/'; // Don't use react-router. The page should refresh to set envId again like default.
          }}
        >
          Switch Out
        </Button>,
        <Button
          key="admin_modal_ok"
          type="primary"
          disabled={!searchInputValue}
          onClick={() => {
            if (searchInputValue) {
              setEnvId(searchInputValue);
              localStorage.setItem('adminInEnvId', searchInputValue);
              history.push('/');
            }
            setOpen(false);
          }}
        >
          Ok
        </Button>
      ]}
    >
      Search by email or envId:
      <Select
        loading={loading}
        disabled={loading}
        style={{
          width: '100%'
        }}
        showSearch
        onSearch={handleSearch}
        onChange={(value) => setSearchInputValue(value)}
        filterOption={false}
        optionLabelProp="label"
        listHeight={500}
      >
        {searchResults.map((u) => {
          const email = u.Attributes?.find((a) => a.Name === 'email')?.Value;
          const envIds = u.Attributes?.find(
            (a) => a.Name === 'custom:envIds'
          )?.Value;
          return (
            <Select.Option key={u.Username} value={envIds} label={email}>
              <div>
                {email}
                <br />
                <Typography.Text type="secondary" copyable>
                  {envIds}
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  Created at: {u.UserCreateDate?.toDateString()}
                </Typography.Text>
              </div>
            </Select.Option>
          );
        })}
      </Select>
      {adminEnvId && (
        <Paragraph style={{ marginTop: '10px' }}>
          Current account: <Text code>{adminEnvId}</Text>
        </Paragraph>
      )}
    </Modal>
  );
};

export default AdminTools;
